import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Copy, Gift, Sparkles, FileText, Download, User as UserIcon, Save, X, Users, Check, Clock, Calendar, Share2, Pencil, Wallet, RotateCcw } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { rebookFromBooking } from "../lib/rebook";
import { BACKEND_URL } from "../lib/api";
import EditBookingModal from "../components/EditBookingModal";
import EditGroupBookingModal from "../components/EditGroupBookingModal";

const STATUS_COLOR = {
    pending: "bg-yellow-500/10 text-amber-700 border-yellow-500/30",
    approved: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    processing: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    shipped: "bg-cyan-500/10 text-cyan-700 border-cyan-500/30",
    delivered: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    completed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    cancelled: "bg-red-500/10 text-red-700 border-red-500/30",
};

export default function UserDashboard() {
    const { user, refresh, updateProfile } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [rebookingId, setRebookingId] = useState(null);
    const bookAgain = async (b) => {
        setRebookingId(b.id);
        try {
            const res = await rebookFromBooking(b, { userEmail: user?.email, minAmount: settings.min_booking_amount ?? 80 });
            if (res.empty) { toast.error("These services are no longer on our menu — pick something new."); navigate("/book"); return; }
            if (res.skipped.length) toast(`No longer available: ${res.skipped.join(", ")}`);
            else toast.success(res.target === "/checkout" ? "Same services added — just pick a date & time" : "Same services added — review and continue");
            navigate(res.target);
        } catch {
            toast.error("Couldn't rebook right now. Please try again.");
        } finally {
            setRebookingId(null);
        }
    };
    const [bookings, setBookings] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [creditLedger, setCreditLedger] = useState({ balance: 0, entries: [] });
    const [orders, setOrders] = useState([]);
    const [redeemPts, setRedeemPts] = useState(100);
    const [profileOpen, setProfileOpen] = useState(false);
    const [referrals, setReferrals] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editingGroupId, setEditingGroupId] = useState(null);

    const load = async () => {
        // Resilient fan-out: any single endpoint failure (e.g. /me/referrals not yet redeployed)
        // must NOT prevent the other sections from rendering. We use allSettled and assign per-result.
        const results = await Promise.allSettled([
            api.get("/bookings/me"),
            api.get("/coupons"),
            api.get("/invoices/me"),
            api.get("/orders/me"),
            api.get("/me/referrals"),
            api.get("/me/credit-ledger"),
        ]);
        const [b, c, i, o, r, l] = results;
        if (b.status === "fulfilled") setBookings(b.value.data);
        if (c.status === "fulfilled") setCoupons(c.value.data);
        if (i.status === "fulfilled") setInvoices(i.value.data);
        if (o.status === "fulfilled") setOrders(o.value.data);
        // Always set a referrals object so the empty-state widget can render even if the
        // endpoint hasn't shipped yet (defaults to total: 0).
        setReferrals(r.status === "fulfilled" ? r.value.data : { referrals: [], total: 0, completed_count: 0 });
        if (l.status === "fulfilled") setCreditLedger(l.value.data || { balance: 0, entries: [] });
    };

    useEffect(() => {
        load();
        refresh?.();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const copyRef = () => {
        navigator.clipboard.writeText(user.referral_code);
        toast.success("Referral code copied");
    };

    const shareRef = async () => {
        const url = window.location.origin;
        const code = user.referral_code;
        const text = `Try The Tresses — use code ${code} when you sign up and we both get S$10 off after your first service ✨`;
        const shareData = { title: "The Tresses — Referral", text, url };
        try {
            if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                await navigator.share(shareData);
                toast.success("Thanks for sharing!");
            } else {
                await navigator.clipboard.writeText(`${text} ${url}`);
                toast.success("Invite text copied — paste it anywhere");
            }
        } catch (err) {
            if (err && err.name !== "AbortError") {
                try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Invite text copied"); }
                catch { toast.error("Couldn't share — try Copy code instead"); }
            }
        }
    };

    const redeem = async () => {
        try {
            const { data } = await api.post("/loyalty/redeem", { points: redeemPts });
            toast.success(`Coupon ${data.code} unlocked — ${data.discount_pct}% off`);
            await refresh();
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Redemption failed"));
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen pt-32 pb-32 grain" data-testid="user-dashboard">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-overline">Your Sanctuary</div>
                        <h1 className="font-serif-display text-5xl text-[#3D0C1F] mt-3">Hello, <span className="italic gold-text">{user.name.split(" ")[0]}</span></h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setProfileOpen(true)} className="btn-outline-gold !py-2" data-testid="header-profile-edit">
                            <UserIcon size={12} /> Profile
                        </button>
                        <Link to="/book" className="btn-gold" data-testid="dashboard-book-btn">Book Again</Link>
                    </div>
                </div>

                {/* Profile completion banner */}
                {(!user.phone || !user.address_line) && (
                    <div className="mt-8 card-luxe p-5 grain border-[#C29A5B]/30 flex items-center gap-4 flex-wrap" data-testid="profile-incomplete-banner">
                        <UserIcon className="text-[#8F622C] flex-shrink-0" size={20} />
                        <div className="flex-1 min-w-[260px]">
                            <div className="text-sm text-[#3D0C1F]">Complete your profile</div>
                            <div className="text-xs text-[#7A5262] mt-1">
                                Add your {!user.phone && "phone"}{!user.phone && !user.address_line && " & "}{!user.address_line && "address"} so we can send confirmations and personalised receipts.
                            </div>
                        </div>
                        <button onClick={() => setProfileOpen(true)} className="btn-outline-gold !py-2" data-testid="open-profile-edit">Update profile</button>
                    </div>
                )}

                {/* Top stats */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
                    <div className="card-luxe p-8 grain" data-testid="stat-credits">
                        <Wallet className="text-[#8F622C]" size={20} />
                        <div className="text-overline mt-4">Membership Credits</div>
                        <div className="font-serif-display text-5xl gold-text mt-2" data-testid="stat-credit-balance">S${Number(user.credit_balance || 0).toFixed(2)}</div>
                        <div className="text-[0.7rem] text-[#8A6574] mt-2">Apply at checkout on any booking</div>
                        <Link to="/membership" className="btn-outline-gold !px-4 !py-2 mt-4 inline-flex text-[0.65rem]" data-testid="dashboard-membership-link">
                            {(user.credit_balance || 0) > 0 ? "Buy more →" : "Buy Membership →"}
                        </Link>
                    </div>
                    <div className="card-luxe p-8 grain" data-testid="stat-loyalty">
                        <Sparkles className="text-[#8F622C]" size={20} />
                        <div className="text-overline mt-4">Loyalty Points</div>
                        <div className="font-serif-display text-5xl gold-text mt-2">{user.loyalty_points}</div>
                        <div className="mt-6 flex gap-2">
                            <input
                                type="number"
                                min={100}
                                step={50}
                                value={redeemPts}
                                onChange={(e) => setRedeemPts(parseInt(e.target.value || 0))}
                                className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm w-24"
                                data-testid="redeem-points-input"
                            />
                            <button onClick={redeem} className="btn-outline-gold !px-4 !py-2" data-testid="redeem-points-btn">Redeem</button>
                        </div>
                        <div className="text-[0.7rem] text-[#8A6574] mt-2">10 pts = S$1 discount · min 100 pts</div>
                    </div>

                    <div className="card-luxe p-8 grain" data-testid="stat-referral">
                        <Gift className="text-[#8F622C]" size={20} />
                        <div className="text-overline mt-4">Your Referral Code</div>
                        <div className="font-serif-display text-3xl text-[#3D0C1F] mt-2 tracking-wider">{user.referral_code}</div>
                        <p className="text-xs text-[#7A5262] mt-3">Share with as many friends as you like. When each one completes their first service, you <span className="text-[#8F622C]">both</span> earn 100 points <span className="text-[#8A6574]">(= S$10 off)</span>.</p>
                        <div className="flex flex-wrap gap-2 mt-5">
                            <button onClick={shareRef} className="btn-gold !px-4 !py-2 text-[0.65rem]" data-testid="share-referral">
                                <Share2 size={12} /> Share my code
                            </button>
                            <button onClick={copyRef} className="btn-outline-gold !px-4 !py-2" data-testid="copy-referral">
                                <Copy size={12} /> Copy
                            </button>
                        </div>
                    </div>

                    <div className="card-luxe p-8 grain" data-testid="stat-bookings">
                        <FileText className="text-[#8F622C]" size={20} />
                        <div className="text-overline mt-4">Lifetime Bookings</div>
                        <div className="font-serif-display text-5xl gold-text mt-2">{user.total_bookings}</div>
                        <p className="text-xs text-[#7A5262] mt-3">Thank you for choosing us, again and again.</p>
                    </div>
                </div>

                {/* My Referrals */}
                {referrals && referrals.total > 0 && (
                    <div className="mt-16">
                        <div className="flex items-end justify-between mb-6">
                            <div>
                                <h2 className="text-overline">My Referrals</h2>
                                <p className="text-xs text-[#8A6574] mt-1">
                                    {referrals.completed_count} of {referrals.total} friend{referrals.total === 1 ? "" : "s"} have completed a service
                                </p>
                            </div>
                            <div className="text-[0.65rem] text-[#8F622C] tracking-[0.2em] uppercase">
                                +{referrals.completed_count * 100} pts earned
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-referrals">
                            {referrals.referrals.map((r, idx) => {
                                const isDone = r.status === "completed";
                                const isBooked = r.status === "booked";
                                const Icon = isDone ? Check : isBooked ? Calendar : Clock;
                                const ringClass = isDone
                                    ? "border-[#C29A5B]/55 bg-[#6B1535]/[0.06]"
                                    : "border-[#6B1535]/15";
                                const pillClass = isDone
                                    ? "bg-[#6B1535] text-[#FFF8F2] border-[#C29A5B]"
                                    : isBooked
                                        ? "border-blue-400/40 text-blue-700 bg-blue-400/[0.06]"
                                        : "border-[#6B1535]/15 text-[#7A5262]";
                                return (
                                    <div
                                        key={r.email_masked + idx}
                                        className={`grain p-4 border ${ringClass} flex items-center gap-3`}
                                        data-testid={`referral-row-${idx}`}
                                    >
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full border ${isDone ? "border-[#C29A5B]/40 bg-[#6B1535]/15 text-[#8F622C]" : "border-[#6B1535]/15 bg-[#6B1535]/[0.04] text-[#7A5262]"} flex items-center justify-center font-serif-display text-lg`}>
                                            {r.name_initial}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-[#3D0C1F] truncate">{r.first_name}</div>
                                            <div className="text-[0.65rem] text-[#8A6574] truncate">{r.email_masked}</div>
                                        </div>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[0.6rem] tracking-[0.18em] uppercase border ${pillClass} whitespace-nowrap`}>
                                            <Icon size={9} />
                                            {isDone ? "+100" : isBooked ? "Booked" : "Joined"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {referrals && referrals.total === 0 && (
                    <div className="mt-16" data-testid="my-referrals-empty-section">
                        <div className="mb-6">
                            <h2 className="text-overline">My Referrals</h2>
                            <p className="text-xs text-[#8A6574] mt-1">No friends referred yet</p>
                        </div>
                        <div className="card-luxe grain p-8 flex items-center gap-4" data-testid="my-referrals-empty">
                            <div className="flex-shrink-0 w-12 h-12 rounded-full border border-[#C29A5B]/30 bg-[#6B1535]/[0.06] flex items-center justify-center">
                                <Users size={18} className="text-[#8F622C]" />
                            </div>
                            <div className="flex-1">
                                <div className="text-sm text-[#3D0C1F]">Refer a friend to start earning</div>
                                <div className="text-xs text-[#7A5262] mt-1">Share <span className="text-[#8F622C] font-semibold">{user.referral_code}</span> — when each friend completes their first service, you both get 100 points (S$10 off).</div>
                            </div>
                            <button
                                type="button"
                                onClick={shareRef}
                                className="btn-gold !px-4 !py-2 text-[0.65rem] flex-shrink-0"
                                data-testid="share-referral-empty"
                            >
                                <Share2 size={12} /> Share
                            </button>
                        </div>
                    </div>
                )}

                {/* Bookings */}
                <h2 className="text-overline mt-16 mb-6">Your Bookings</h2>
                <div className="card-luxe overflow-x-auto" data-testid="bookings-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[#8A6574] border-b border-[#6B1535]/10">
                                <th className="px-6 py-4 text-overline">Service</th>
                                <th className="px-6 py-4 text-overline">Date</th>
                                <th className="px-6 py-4 text-overline">Time</th>
                                <th className="px-6 py-4 text-overline">Status</th>
                                <th className="px-6 py-4 text-overline text-right">Total</th>
                                <th className="px-6 py-4 text-overline text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.length === 0 && (
                                <tr><td colSpan="6" className="text-center px-6 py-12 text-[#7A5262]">No bookings yet. <Link to="/book" className="text-[#8F622C]">Book one now →</Link></td></tr>
                            )}
                            {bookings.map((b) => {
                                let editable = false;
                                if (b.status === "pending") {
                                    try {
                                        const dt = new Date(`${b.date}T${b.time}:00`);
                                        editable = (dt.getTime() - Date.now()) > 2 * 60 * 60 * 1000;
                                    } catch { editable = false; }
                                }
                                return (
                                    <tr key={b.id} className="border-b border-[#6B1535]/10 hover:bg-[#6B1535]/[0.02]" data-testid={`booking-row-${b.id}`}>
                                        <td className="px-6 py-4">
                                            <div className="text-[#3D0C1F] font-serif-display text-lg">{b.service_name}</div>
                                            {b.promotion_title && (
                                                <div className="mt-1 inline-flex items-center gap-1.5 text-[0.6rem] tracking-[0.18em] uppercase text-[#8F622C] bg-[#6B1535]/10 border border-[#C29A5B]/30 px-2 py-0.5" data-testid={`booking-package-${b.id}`}>
                                                    <Sparkles size={9} /> Package: {b.promotion_title}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-[#7A5262]">{b.date}</td>
                                        <td className="px-6 py-4 text-[#7A5262]">{b.time}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 text-[0.65rem] tracking-[0.2em] uppercase border ${STATUS_COLOR[b.status]}`}>{b.status}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right text-[#8F622C]">S${b.final_price}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex flex-col items-end gap-2">
                                            <button onClick={() => bookAgain(b)} disabled={rebookingId === b.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#C29A5B] text-[0.62rem] tracking-[0.2em] uppercase text-[#6B1535] hover:bg-[#6B1535] hover:text-[#FFF8F2] hover:border-[#6B1535] transition-colors disabled:opacity-50 whitespace-nowrap" data-testid={`book-again-${b.id}`}>
                                                <RotateCcw size={11} /> {rebookingId === b.id ? "Adding…" : "Book again"}
                                            </button>
                                            {editable ? (
                                                <div className="inline-flex flex-col items-end gap-1">
                                                    <button onClick={() => setEditing(b)} className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C] hover:text-[#3D0C1F]" data-testid={`edit-booking-${b.id}`}>
                                                        <Pencil size={11}/> Edit
                                                    </button>
                                                    {b.is_group_initiator && b.group_id && (
                                                        <button onClick={() => setEditingGroupId(b.group_id)} className="inline-flex items-center gap-1.5 text-[0.6rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#8F622C]" data-testid={`edit-group-${b.group_id}`}>
                                                            <Users size={10}/> Edit whole group
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-[0.6rem] text-[#8A6574]" data-testid={`edit-booking-locked-${b.id}`}>
                                                    {b.status === "pending" ? "<2 h away" : "Locked"}
                                                </span>
                                            )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Orders */}
                <h2 className="text-overline mt-16 mb-6">Shop Orders</h2>
                <div className="card-luxe overflow-x-auto" data-testid="orders-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[#8A6574] border-b border-[#6B1535]/10">
                                <th className="px-6 py-4 text-overline">Items</th>
                                <th className="px-6 py-4 text-overline">Date</th>
                                <th className="px-6 py-4 text-overline">Status</th>
                                <th className="px-6 py-4 text-overline text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.length === 0 && (
                                <tr><td colSpan="4" className="text-center px-6 py-12 text-[#7A5262]">No orders yet. <Link to="/shop" className="text-[#8F622C]">Visit shop →</Link></td></tr>
                            )}
                            {orders.map((o) => (
                                <tr key={o.id} className="border-b border-[#6B1535]/10 hover:bg-[#6B1535]/[0.02]" data-testid={`order-row-${o.id}`}>
                                    <td className="px-6 py-4">
                                        {o.items.map((it) => (
                                            <div key={it.product_id} className="text-[#3D0C1F] text-sm">{it.quantity} × {it.name}</div>
                                        ))}
                                    </td>
                                    <td className="px-6 py-4 text-[#7A5262]">{new Date(o.created_at).toLocaleDateString()}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 text-[0.65rem] tracking-[0.2em] uppercase border ${STATUS_COLOR[o.status] || STATUS_COLOR.pending}`}>{o.status}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right text-[#8F622C]">S${o.total}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Coupons */}
                <h2 className="text-overline mt-16 mb-6">Available Coupons</h2>
                <div className="grid md:grid-cols-3 gap-4" data-testid="coupons-grid">
                    {coupons.map((c) => (
                        <div key={c.id} className="card-luxe p-6 grain" data-testid={`coupon-${c.code}`}>
                            <div className="flex justify-between items-start">
                                <div className="font-serif-display text-2xl gold-text tracking-wider">{c.code}</div>
                                <div className="text-[#8F622C] font-serif-display text-3xl">{c.discount_pct}%</div>
                            </div>
                            <p className="text-sm text-[#7A5262] mt-3">{c.description}</p>
                        </div>
                    ))}
                </div>

                {/* Invoices */}
                <h2 className="text-overline mt-16 mb-6">Invoices</h2>
                <div className="card-luxe overflow-x-auto" data-testid="invoices-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[#8A6574] border-b border-[#6B1535]/10">
                                <th className="px-6 py-4 text-overline">Invoice #</th>
                                <th className="px-6 py-4 text-overline">Service</th>
                                <th className="px-6 py-4 text-overline">Issued</th>
                                <th className="px-6 py-4 text-overline text-right">Credits</th>
                                <th className="px-6 py-4 text-overline text-right">Amount</th>
                                <th className="px-6 py-4 text-overline text-right">PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length === 0 && (
                                <tr><td colSpan="6" className="text-center px-6 py-12 text-[#7A5262]">Invoices will appear here once your services are completed.</td></tr>
                            )}
                            {invoices.map((i) => {
                                const token = localStorage.getItem("tresses_token");
                                const pdfUrl = `${BACKEND_URL}/api/invoices/${i.id}/pdf`;
                                const creditsUsedInv = Number(i.credits_used) || 0;
                                const creditsLeftInv = Number(i.credits_remaining_after) || 0;
                                return (
                                    <tr key={i.id} className="border-b border-[#6B1535]/10 hover:bg-[#6B1535]/[0.02]" data-testid={`invoice-row-${i.id}`}>
                                        <td className="px-6 py-4 text-[#8F622C] font-mono text-xs">{i.id}</td>
                                        <td className="px-6 py-4 text-[#3D0C1F]">{i.service_name}</td>
                                        <td className="px-6 py-4 text-[#7A5262]">{new Date(i.issued_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-right" data-testid={`invoice-credits-${i.id}`}>
                                            {creditsUsedInv > 0 ? (
                                                <div className="text-[0.7rem] leading-tight">
                                                    <div className="text-[#8F622C]">−S${creditsUsedInv.toFixed(2)}</div>
                                                    <div className="text-[#8A6574]">S${creditsLeftInv.toFixed(2)} left</div>
                                                </div>
                                            ) : (
                                                <span className="text-[#8A6574] text-[0.7rem]">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right text-[#8F622C]">S${i.final_price}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
                                                        if (!res.ok) throw new Error();
                                                        const blob = await res.blob();
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement("a");
                                                        a.href = url; a.download = `${i.id}.pdf`; a.click();
                                                        URL.revokeObjectURL(url);
                                                    } catch { toast.error("Unable to download PDF"); }
                                                }}
                                                className="inline-flex items-center gap-2 text-[#8F622C] hover:text-[#3D0C1F] text-xs uppercase tracking-[0.2em]"
                                                data-testid={`download-invoice-${i.id}`}
                                            >
                                                <Download size={12} /> Download
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Credit Ledger — full history of membership credit events */}
            {creditLedger.entries.length > 0 && (
                <div className="mt-16" data-testid="credit-ledger-section">
                    <div className="flex items-baseline justify-between mb-6">
                        <div>
                            <h2 className="font-serif-display text-3xl text-[#3D0C1F]">Credit History</h2>
                            <p className="text-[0.7rem] text-[#8A6574] mt-1">Every credit earned and spent. Full transparency.</p>
                        </div>
                        <div className="text-right">
                            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Current Balance</div>
                            <div className="font-serif-display text-2xl gold-text" data-testid="credit-ledger-balance">S${Number(creditLedger.balance || 0).toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="card-luxe overflow-x-auto" data-testid="credit-ledger-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[#8A6574] border-b border-[#6B1535]/10">
                                    <th className="px-6 py-4 text-overline">Date</th>
                                    <th className="px-6 py-4 text-overline">Activity</th>
                                    <th className="px-6 py-4 text-overline">Reference</th>
                                    <th className="px-6 py-4 text-overline text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditLedger.entries.map((e, idx) => (
                                    <tr key={`${e.type}-${e.reference}-${idx}`} className="border-b border-[#6B1535]/10 hover:bg-[#6B1535]/[0.02]" data-testid={`credit-ledger-row-${idx}`}>
                                        <td className="px-6 py-4 text-[#7A5262] whitespace-nowrap">{e.date ? new Date(e.date).toLocaleDateString() : "—"}</td>
                                        <td className="px-6 py-4 text-[#3D0C1F]">
                                            <div className="flex items-center gap-2">
                                                {e.type === "credit"
                                                    ? <Wallet size={12} className="text-emerald-700" />
                                                    : <Sparkles size={12} className="text-[#8F622C]" />}
                                                <span>{e.label}</span>
                                            </div>
                                            {e.type === "credit" && e.expires_at && (
                                                <div className="text-[0.65rem] text-[#8A6574] mt-1 pl-5">Expires {new Date(e.expires_at).toLocaleDateString()}</div>
                                            )}
                                            {e.type === "debit" && e.meta?.booking_date && (
                                                <div className="text-[0.65rem] text-[#8A6574] mt-1 pl-5">Appointment {new Date(e.meta.booking_date).toLocaleDateString()} · {e.meta.booking_time}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-[#8A6574] font-mono text-[0.65rem]">{(e.reference || "").slice(0, 8)}</td>
                                        <td className={`px-6 py-4 text-right font-medium whitespace-nowrap ${e.amount >= 0 ? "text-emerald-700" : "text-[#8F622C]"}`}>
                                            {e.amount >= 0 ? "+" : "−"}S${Math.abs(e.amount).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {profileOpen && (
                <ProfileEditModal user={user} onClose={() => setProfileOpen(false)} updateProfile={updateProfile} />
            )}
            <EditBookingModal
                booking={editing}
                open={!!editing}
                onClose={() => setEditing(null)}
                onSaved={(updated) => setBookings((prev) => prev.map((b) => b.id === updated.id ? updated : b))}
                isAdmin={false}
            />
            <EditGroupBookingModal
                groupId={editingGroupId}
                members={editingGroupId ? bookings.filter((b) => b.group_id === editingGroupId) : []}
                open={!!editingGroupId}
                onClose={() => setEditingGroupId(null)}
                onSaved={({ payload }) => {
                    setBookings((prev) => prev.map((b) => b.group_id === editingGroupId ? { ...b, ...payload } : b));
                    setEditingGroupId(null);
                }}
                isAdmin={false}
            />
        </div>
    );
}

function ProfileEditModal({ user, onClose, updateProfile }) {
    const [form, setForm] = useState({
        name: user.name || "",
        phone: (user.phone || "").replace(/^\+65/, "").replace(/\D/g, "").slice(0, 8),
        address_line: user.address_line || "",
        city: user.city || "",
        postal_code: user.postal_code || "",
        country: user.country || "Singapore",
    });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if (!(form.phone.length === 8 && /^[689]/.test(form.phone))) {
            toast.error("Enter a valid 8-digit Singapore phone number starting with 6, 8, or 9");
            return;
        }
        setSaving(true);
        try {
            await updateProfile({ ...form, phone: `+65${form.phone}` });
            toast.success("Profile updated");
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Update failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3D0C1F]/45 backdrop-blur-sm px-4" data-testid="profile-edit-modal">
            <form onSubmit={save} className="relative max-w-lg w-full bg-[#FDF0EC] border border-[#C29A5B]/40 grain p-8 max-h-[90vh] overflow-y-auto">
                <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#7A5262] hover:text-[#8F622C]" data-testid="profile-close"><X size={20} /></button>
                <div className="text-overline">Profile</div>
                <h3 className="font-serif-display text-3xl text-[#3D0C1F] mt-3">Your details.</h3>
                <p className="text-sm text-[#7A5262] mt-2">Used on bookings, confirmations, and invoices.</p>

                <div className="mt-6 space-y-4">
                    <div>
                        <label className="text-overline">Full name</label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                            data-testid="profile-name" />
                    </div>
                    <div>
                        <label className="text-overline">Phone</label>
                        <div className="flex mt-2">
                            <span className="inline-flex items-center px-3 bg-[#FDF0EC] border border-r-0 border-[#6B1535]/15 text-[#8F622C] text-sm font-medium">+65</span>
                            <input
                                type="tel"
                                required
                                inputMode="numeric"
                                maxLength={8}
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                                placeholder="9123 4567"
                                className="flex-1 bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F] tracking-wider"
                                data-testid="profile-phone"
                            />
                        </div>
                        <p className="text-[0.65rem] text-[#8A6574] mt-1">8 digits, must start with 6, 8, or 9</p>
                    </div>
                    <div>
                        <label className="text-overline">Address line</label>
                        <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })}
                            className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                            data-testid="profile-address" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-overline">City</label>
                            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                                className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="profile-city" />
                        </div>
                        <div>
                            <label className="text-overline">Postal</label>
                            <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                                className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="profile-postal" />
                        </div>
                    </div>
                    <div>
                        <label className="text-overline">Country</label>
                        <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                            className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                            data-testid="profile-country" />
                    </div>
                </div>

                <div className="flex gap-3 mt-8">
                    <button type="button" onClick={onClose} className="btn-outline-gold flex-1 justify-center" data-testid="profile-cancel">Cancel</button>
                    <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center disabled:opacity-50" data-testid="profile-save">
                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </form>
        </div>
    );
}
